import React from 'react';
import { motion } from "framer-motion";
import { Link, useParams } from 'react-router-dom';

const blogContent = {
  "tables-chairs-conversations": {
    title: "Tables, Chairs & Conversations: Designing the Heart of the Home",
    category: "Furniture Guide",
    date: "June 12, 2023",
    image: "/Blogs_1.png",
    metaTitle: "Tables, Chairs & Conversations | Mi Casa Su Casa Furniture Collection",
    metaDescription: "Explore Mi Casa Su Casa's tables & chairs—crafted to spark connection, from statement coffee tables to wooden side tables, round & bed tables online.",
    content: `
      <p>In every home, there's a spot where life unfolds—where conversations flow as easily as coffee is poured, where laughter lingers long after dinner, and where friends and family feel instantly at ease. This magical space is often shaped by the furniture we choose: a dining table that welcomes all, chairs that invite you to stay a little longer, and side tables that hold your stories as much as your tea cups.</p>

      <p>At Mi Casa Su Casa, we believe that furniture is more than functional—it's emotional. Every table and chair in our collection is designed not just to fill a space, but to spark connection, inspire comfort, and reflect your personal style. From statement coffee tables to sculptural chairs, our designs are crafted to be the heartbeats of your home.</p>

      <div class="my-8">
        <img src="/blog1img.png" alt="Beautiful dining room setup" class="w-full h-auto object-contain rounded-lg" />
      </div>

      <h2>Why the Right Tables & Chairs Matter</h2>

      <p>Furniture often acts as the silent host in your home—it sets the mood, directs the flow, and subtly encourages togetherness. A table, for example, can transform a quick meal into a shared ritual, while a well-placed chair can turn a corner into your favorite reading nook.</p>

      <p>Here's why thoughtful design matters:</p>

      <ul>
        <li><strong>Comfort sparks connection</strong> – If your seating feels good, people linger. That extra ten minutes might be when a great conversation happens.</li>
        <li><strong>Aesthetic sets the tone</strong> – A sculptural coffee table or uniquely shaped dining chair signals warmth, creativity, or sophistication before a single word is spoken.</li>
        <li><strong>Function supports flow</strong> – The right size and shape can make hosting effortless and everyday life more harmonious.</li>
      </ul>

      <h2>Coffee Tables: The Social Anchor of the Living Room</h2>

      <p>Picture this: guests arrive, you pour coffee, and everyone gathers around a table that feels as welcoming as the conversation itself. Coffee tables do more than hold mugs—they anchor your seating arrangement, draw people together, and often spark the "where did you get that?" moment.</p>

      <p>Our coffee tables for living room come in designs ranging from sleek minimalism to organic wood textures. Whether you prefer round tables that invite intimate chats or bold, angular silhouettes that make a style statement, each piece is built to complement both your decor and your lifestyle.</p>

      <div class="styling-tip">
        <p><strong>STYLING TIP:</strong> Pair a low-profile coffee table with plush sofas and textured rugs for a cozy conversation pit. Add a stack of beautiful books or a sculptural vase for personality.</p>
      </div>

      <h2>Side Tables: Small Size, Big Impact</h2>

      <p>If the coffee table is the heart, side tables are the heartbeat—they quietly keep things moving, holding drinks, snacks, lamps, or even a book you've been meaning to finish.</p>

      <p>At Mi Casa Su Casa, we offer wooden side tables, bed tables online, and end tables that blend form and function seamlessly. Think of them as the supporting actors that often steal the show:</p>

      <ul>
        <li>A round side table in the living room to hold your evening tea.</li>
        <li>A bed table with storage to keep your nighttime essentials close.</li>
        <li>A minimalist end table beside your reading chair for that perfect lighting setup.</li>
      </ul>

      <p><strong>Why We Love Them:</strong> Side tables are easy to move, style, and swap, giving you the flexibility to refresh your space without a complete makeover.</p>

      <h2>Dining Tables: Where Meals Become Memories</h2>

      <p>Dining tables are where birthdays are celebrated, plans are made, and stories are told over steaming plates of food. The right table doesn't just fit your dining space—it fits your life.</p>

      <p>Our dining table designs range from round tables that encourage eye contact and laughter to larger rectangular pieces perfect for big family gatherings. We believe in crafting surfaces that not only look beautiful but can withstand years of shared moments.</p>

      <div class="styling-tip">
        <p><strong>PRO TIP:</strong> If your dining area is compact, choose a round table with a pedestal base—it maximizes legroom and keeps the vibe intimate.</p>
      </div>

      <h2>Chairs: Comfort in Every Curve</h2>

      <p>A great chair is one you don't want to get up from. Whether it's a dining chair that supports hours of lively debate, a lounge chair for quiet reflection, or a sculptural accent chair that becomes an instant conversation starter, seating is central to creating a welcoming home.</p>

      <p>At Mi Casa Su Casa, our chairs are designed with ergonomics and artistry in mind. Every curve, cushion, and contour is intentional, offering both style and long-lasting comfort.</p>

      <h2>Designing for Connection: Our Philosophy</h2>

      <p>We know that the most memorable moments happen in comfortable, beautiful spaces. That's why our tables and chairs are designed to:</p>

      <ul>
        <li><strong>Encourage Gathering</strong> – Shapes and sizes that naturally bring people closer together.</li>
        <li><strong>Blend Versatility & Personality</strong> – Pieces that adapt to different occasions but never lose their character.</li>
        <li><strong>Last for Years</strong> – High-quality materials that age gracefully, carrying your stories forward.</li>
        <li><strong>Reflect Your Style</strong> – From warm woods to modern metals, our collections suit every design aesthetic.</li>
      </ul>

      <h2>Inspiring Corners: How to Use Tables & Chairs Creatively</h2>

      <p>Not every gathering happens at the dining table—sometimes the best conversations happen in unexpected corners. Here are a few ideas:</p>

      <ul>
        <li><strong>The Reading Corner</strong> – A cozy armchair paired with a wooden side table and a floor lamp.</li>
        <li><strong>The Balcony Retreat</strong> – A set of sculptural chairs and a small round table for morning coffee.</li>
        <li><strong>The Work-From-Home Zone</strong> – A sleek bed table for laptop work that transitions to dinner mode.</li>
        <li><strong>The Lounge Space</strong> – A bold coffee table for living room surrounded by low, deep seating for casual hangouts.</li>
      </ul>

      <h2>Our Bestsellers & Why They're Loved</h2>

      <ul>
        <li><strong>Wooden Side Tables</strong> – Handcrafted with natural grains that bring warmth and texture.</li>
        <li><strong>Round Tables</strong> – Perfect for conversation flow and space efficiency.</li>
        <li><strong>End Tables</strong> – Compact, stylish, and endlessly versatile.</li>
        <li><strong>Bed Tables Online</strong> – Blending function with elegance for bedrooms that feel indulgent.</li>
        <li><strong>Coffee Tables for Living Room</strong> – Anchoring spaces with designs that are both timeless and fresh.</li>
      </ul>

      <h2>Sustainability Meets Style</h2>

      <p>At Mi Casa Su Casa, we believe beauty should be kind—to both people and the planet. Many of our pieces are made from responsibly sourced wood and eco-conscious materials. This means you can create a home you love while making choices that matter for the future.</p>

      <h2>Conclusion</h2>

      <p>When you choose the right tables and chairs, you're not just furnishing your home—you're designing the backdrop for your life. Every cup of tea, every heartfelt chat, every burst of laughter finds its stage here.</p>

      <p>Whether it's a statement coffee table that catches every eye, a round table that brings people closer, or a wooden side table that quietly supports your daily rituals, Mi Casa Su Casa's collection is built for real homes and real moments.</p>

      <p><strong>Because at the end of the day, the heart of the home isn't just where you live—it's where you connect. And that connection starts with a place to sit, a place to gather, and a place that feels entirely your own.</strong></p>
    `
  },
  "lighting-the-way": {
    title: "Lighting the Way: When Lamps Become Works of Art",
    category: "Design Fusion",
    date: "May 28, 2023",
    image: "/Blogs_2.png",
    metaTitle: "Lighting the Way | Artistic Lamps, Chandeliers & Pendant Lights | Mi Casa Su Casa",
    metaDescription: "Discover Mi Casa Su Casa's lamps, chandeliers, pendant & outdoor lights—design statements that add warmth, drama, and personality to your home.",
    content: `
      <p>Lighting is more than just a practical necessity—it's the invisible magic that shapes the mood of a room. A perfectly placed lamp can transform a shadowy corner into a cozy reading nook, a chandelier can turn a dining room into a grand stage, and pendant lights can bring intimacy to a kitchen island.</p>

      <p>But when lighting doubles as a work of art, it becomes something far more powerful. It not only illuminates—it inspires.</p>

      <p>At Mi Casa Su Casa, we believe light is a storyteller. Our curated collection of lamps, chandeliers, pendant lights, and outdoor lights is designed not just to brighten spaces, but to add warmth, drama, and personality to your home.</p>

      <div class="my-8">
        <img src="/blog12img.jpg" alt="Artistic lighting collection" class="w-full h-auto object-contain rounded-lg" />
      </div>

      <h2>The Power of Artistic Lighting</h2>

      <p>Think of a room as a blank canvas—walls, furniture, and decor form the foundation, but lighting is the brushstroke that gives it depth and emotion.</p>

      <p>Artistic lighting pieces are conversation starters. They express your style as much as any painting or sculpture, yet they also serve an essential purpose. Whether it's a sleek modern table lamp, a sculptural floor lamp, or a shimmering chandelier, these pieces bring together form and function in a way few decor items can.</p>

      <p>Why artistic lighting matters:</p>

      <ul>
        <li><strong>Creates ambiance</strong> – Warm glows for relaxation, crisp light for focus, or dramatic shadows for mood.</li>
        <li><strong>Defines spaces</strong> – Lighting helps visually separate different areas without walls.</li>
        <li><strong>Adds personality</strong> – A bold lamp can signal creativity, sophistication, or playfulness.</li>
      </ul>

      <h2>Table Lamps: Small in Size, Big in Style</h2>

      <p>A table lamp may be modest in scale, but it can completely change the way a room feels. Beyond providing task lighting for reading or work, it becomes a key part of your styling story.</p>

      <p>At Mi Casa Su Casa, our table lamps range from sleek minimalist designs to ornate, artistic forms. Whether you're placing one on a bedside table, console, or desk, the right lamp does more than light up your space—it anchors the mood.</p>

      <div class="styling-tip">
        <p><strong>STYLING TIP:</strong> Pair a ceramic sculptural lamp with a neutral shade to create texture contrast, or choose a metallic base to add a hint of luxury.</p>
      </div>

      <h2>Floor Lamps: Functional Sculptures</h2>

      <p>A floor lamp can act like a piece of modern art that just happens to light your space. These taller pieces bring both vertical interest and practical illumination to a room.</p>

      <p>From mid-century tripod lamps to dramatic arc designs, our floor lamps are meant to be seen. They can fill an empty corner with personality, highlight architectural details, or serve as a statement piece in your living room.</p>

      <div class="styling-tip">
        <p><strong>PRO TIP:</strong> Place a floor lamp next to an armchair and side table for a reading zone that feels intentional and inviting.</p>
      </div>

      <h2>Chandeliers: The Centerpiece of Elegance</h2>

      <p>If lighting were jewelry, the chandelier would be the crown jewel. It commands attention, reflects light beautifully, and instantly elevates any room.</p>

      <p>Our chandeliers range from classic crystal styles to bold contemporary designs with clean lines and unexpected materials. Whether in the dining room, living room, or even a bedroom, a chandelier doesn't just light up a space—it becomes the focal point.</p>

      <div class="styling-tip">
        <p><strong>DESIGN INSIGHT:</strong> Hang your chandelier so the bottom sits about 30–36 inches above the dining table for perfect proportions and balanced light.</p>
      </div>

      <h2>Pendant Lights: Versatile & Striking</h2>

      <p>Pendant lights have an almost magical ability to transform the atmosphere of a room. They work equally well in clusters over a kitchen island, as a single statement above a dining table, or as bedside lighting for a modern twist.</p>

      <p>At Mi Casa Su Casa, our pendant lights are crafted with attention to detail, ensuring they deliver both visual impact and perfect illumination. Glass pendants bring softness and warmth, while metal designs add an industrial or modern edge.</p>

      <h2>Outdoor Lights: Extending Beauty Beyond Walls</h2>

      <p>Why should style stop at your front door? Outdoor lighting not only enhances safety but also sets the tone for your home before guests even walk inside.</p>

      <p>Our collection of outdoor lights includes sleek wall sconces, pathway lighting, and statement-making fixtures that bring architectural beauty to your exterior. They're designed to withstand the elements while still adding charm and sophistication.</p>

      <div class="styling-tip">
        <p><strong>TIP:</strong> Combine wall-mounted outdoor lights with low garden lights to create depth and highlight landscaping.</p>
      </div>

      <h2>Lighting as a Design Layer</h2>

      <p>Interior designers often talk about layered lighting—a mix of ambient, task, and accent lighting. This approach ensures your space works for different moods and activities while looking visually rich.</p>

      <ul>
        <li><strong>Ambient lighting</strong> – General illumination, often from ceiling lights or large floor lamps.</li>
        <li><strong>Task lighting</strong> – Focused light for reading, cooking, or working, often from table lamps or pendants.</li>
        <li><strong>Accent lighting</strong> – Highlights art, architecture, or creates drama, often from wall sconces or LED strips.</li>
      </ul>

      <p>Mi Casa Su Casa's collection is designed to make layering easy—pieces that work beautifully on their own and harmoniously together.</p>

      <h2>Materials & Finishes That Tell a Story</h2>

      <p>The artistry in lighting often comes from the materials and finishes. Consider how each element impacts the overall mood:</p>

      <ul>
        <li><strong>Glass</strong> – Diffuses light beautifully for a soft, welcoming glow.</li>
        <li><strong>Metal</strong> – Adds drama, elegance, or industrial chic depending on the finish.</li>
        <li><strong>Ceramic</strong> – Brings artisanal charm and texture.</li>
        <li><strong>Natural fibers</strong> – Rattan or linen shades create organic warmth.</li>
      </ul>

      <p>Our lamps and fixtures are chosen not just for looks, but for the quality of craftsmanship and the way they interact with light.</p>

      <h2>Choosing the Right Artistic Lamp for Your Space</h2>

      <p>When selecting lighting that doubles as art, think about:</p>

      <ul>
        <li><strong>Scale</strong> – The size should suit the space. A tiny lamp on a large table can look lost, while an oversized chandelier in a small room can overwhelm.</li>
        <li><strong>Purpose</strong> – Is it for reading, ambience, or pure style?</li>
        <li><strong>Style Harmony</strong> – Match or contrast your existing decor for the right visual balance.</li>
        <li><strong>Placement</strong> – Ensure it enhances the focal points of your room.</li>
      </ul>

      <h2>Sustainability in Lighting</h2>

      <p>At Mi Casa Su Casa, we believe beauty should be sustainable. Many of our lighting pieces incorporate energy-efficient LED technology, eco-friendly materials, and long-lasting craftsmanship so you can enjoy elegance without compromise.</p>

      <h2>Lighting as Conversation Pieces</h2>

      <p>Perhaps the best part of owning a lamp, chandelier, or pendant that doubles as art is the reaction it inspires. Guests notice it, ask about it, and remember it. These fixtures aren't just functional—they become part of your home's story.</p>

      <p>Whether it's a table lamp that looks like a sculptural object, a chandelier that dazzles with geometric forms, or outdoor lights that glow like lanterns, these are more than sources of light—they're sources of joy.</p>

      <h2>Conclusion</h2>

      <p>Light has the power to make a house feel like a home and when your lighting is as beautiful as it is functional, it becomes part of your personal expression.</p>

      <p>With Mi Casa Su Casa's curated range of lamps, chandeliers, pendant lights, and outdoor lights, you can create spaces that don't just shine—they speak. Because lighting isn't just about seeing—it's about feeling.</p>
    `
  },
  "craft-of-comfort": {
    title: "The Craft of Comfort: Behind the Scenes with Our Artisanal Partners",
    category: "Craftsmanship",
    date: "May 15, 2023",
    image: "/Blogs_3.png",
    metaTitle: "The Craft of Comfort | Mi Casa Su Casa Artisanal Furniture & Décor",
    metaDescription: "Discover the craftsmanship, heritage, and modern design behind Mi Casa Su Casa's artisanal furniture, sculptures, and décor.",
    content: `
      <p>Every piece of furniture and décor tells a story. It carries with it not just form and function, but the heritage, skill, and dedication of the artisans who bring it to life. At Mi Casa Su Casa (MCSC), we believe that comfort extends beyond aesthetics—it lives in the soul of craftsmanship. Our mission is to connect discerning homeowners with extraordinary artisanal creations that blend tradition with contemporary design.</p>

      <p>This blog takes you behind the scenes, introducing the craftsmanship, techniques, and traditions behind our curated furniture and décor collections. Along the way, we spotlight the stories of our partner brands and artisans who shape raw materials into works of timeless beauty.</p>

      <div class="my-8">
        <img src="/blog13img.jpg" alt="Artisanal craftsmanship" class="w-full h-auto object-contain rounded-lg" />
      </div>

      <h2>The Soul of Craftsmanship</h2>

      <p>Craftsmanship is more than the ability to build something; it's an art form passed down through generations. Our artisanal partners often inherit techniques that date back centuries. Whether it's the slow chisel of a sculptor, the delicate brushstroke of a painter, or the precision of a woodworker, their hands breathe life into raw materials.</p>

      <p>What sets artisanal craftsmanship apart from mass production is intentionality. Every curve, join, and finish is purposeful. The imperfections you may spot are not flaws but the unique fingerprints of human artistry. These pieces aren't just decorative; they are imbued with soul, making your living spaces feel alive.</p>

      <h2>Heritage Meets Modern Design</h2>

      <p>One of the most fascinating aspects of working with our partners is seeing how traditional techniques merge seamlessly with modern design aesthetics. For example:</p>

      <ul>
        <li><strong>Hand-Carved Furniture:</strong> Our woodworkers employ age-old carving techniques while adapting their motifs to contemporary tastes. Instead of overly ornate designs, you'll find sleek minimalism paired with subtle traditional flourishes.</li>
        <li><strong>Ceramics and Pottery:</strong> From hand-thrown clay pots to modern glazed finishes, potters create versatile décor items that honor history while fitting into today's interiors.</li>
        <li><strong>Textiles and Weaves:</strong> Traditional weaving methods are preserved while experimenting with new fibers and modern patterns, bridging the old and the new.</li>
      </ul>

      <p>This fusion allows us to offer collections that feel both timeless and relevant, carrying forward legacies without sacrificing style.</p>

      <h2>Behind the Scenes: Techniques that Shape Beauty</h2>

      <h3>1. Woodworking and Joinery</h3>
      <p>Many of our furniture partners rely on mortise and tenon joinery, a traditional technique that avoids nails or screws. This age-old method ensures strength and durability while allowing the natural beauty of wood grains to shine. Each table, chair, and cabinet tells a story carved into oak, teak, or walnut.</p>

      <h3>2. Hand Carving and Sculptures</h3>
      <p>Sculptors in our network still use simple chisels and hammers to bring marble, stone, and wood to life. From intricate deities to abstract art, each sculpture is the result of hours—sometimes weeks—of meticulous dedication. These creations serve as centerpieces that spark conversation and reflection.</p>

      <h3>3. Pottery and Ceramics</h3>
      <p>Artisans use the potter's wheel, a timeless symbol of creation, to craft bowls, vases, and decorative items. The clay is fired in traditional kilns, sometimes wood-fed, imparting unique tones and textures. This slow process ensures that no two pieces are ever identical.</p>

      <h3>4. Textile Weaving and Embroidery</h3>
      <p>Handwoven fabrics remain at the heart of our home décor collection. Whether it's rugs, throws, or cushion covers, weavers employ looms that have stood the test of time. Embroiderers add a touch of storytelling by incorporating motifs inspired by folklore, nature, and local traditions.</p>

      <h3>5. Metalwork and Decorative Accents</h3>
      <p>From bronze statues to intricately cast iron lamps, our metal artisans merge durability with artistry. Techniques like lost-wax casting—used for centuries—are still alive in their workshops, producing decorative items that radiate character.</p>

      <h2>The Human Touch: Stories of Our Artisans</h2>

      <p>Every piece in your home connects you to an artisan's journey. Consider:</p>

      <ul>
        <li>A sculptor in Rajasthan who began carving stone at the age of 10, carrying forward his grandfather's craft.</li>
        <li>A textile artisan in Gujarat who learned weaving from her mother, now introducing eco-friendly threads to adapt to modern needs.</li>
        <li>A woodworker in Kerala who honors local traditions while crafting minimalist furniture that appeals globally.</li>
      </ul>

      <p>These stories give depth to your décor. When you place one of these pieces in your home, you're not just decorating—you're becoming part of a lineage of artistry and passion.</p>

      <h2>Why Handcrafted Matters in Modern Homes</h2>

      <p>In today's fast-paced, mass-produced world, handcrafted furniture and décor offer a refreshing counterbalance. Here's why choosing artisanal creations matters:</p>

      <ul>
        <li><strong>Authenticity:</strong> No two handmade items are exactly alike, giving your home a unique signature.</li>
        <li><strong>Sustainability:</strong> Many artisans use eco-friendly practices, including locally sourced materials and low-waste production methods.</li>
        <li><strong>Durability:</strong> Traditional techniques are time-tested, ensuring longevity and resilience.</li>
        <li><strong>Cultural Preservation:</strong> Supporting artisans keeps heritage crafts alive for future generations.</li>
        <li><strong>Emotional Connection:</strong> Handmade items carry stories, making your living space deeply personal.</li>
      </ul>

      <h2>Spotlight on Mi Casa Su Casa Collections</h2>

      <p>At Mi Casa Su Casa, our collection reflects this philosophy of heritage and modernity. We pride ourselves on offering:</p>

      <ul>
        <li><strong>Art & Craft Pieces:</strong> From intricate sculptures to contemporary artwork that celebrates timeless traditions.</li>
        <li><strong>Sculptures:</strong> Hand-carved and hand-molded sculptures that double as décor and conversation starters.</li>
        <li><strong>Decorative Items:</strong> Unique accents—vases, bowls, lamps, and wall hangings—that infuse elegance into any room.</li>
        <li><strong>Furniture:</strong> Crafted with passion and precision, blending comfort with artisanal detail.</li>
      </ul>

      <p>Every item we curate is a testament to our commitment: to bring homes alive with warmth, soul, and artistry.</p>

      <h2>Blending Comfort with Art</h2>

      <p>The essence of our work lies in what we call "The Craft of Comfort." Comfort doesn't only mean a plush chair or a soft rug. It is the deeper sense of belonging that comes when your surroundings reflect your values and passions.</p>

      <p>When you welcome artisanal creations into your home, you are not just decorating—you are crafting an environment where beauty, heritage, and comfort coexist. Each object becomes a daily reminder of human creativity, patience, and love.</p>

      <h2>How We Work with Artisans</h2>

      <p>We don't just source products—we build partnerships. Our team spends time with artisans in their workshops, learning their methods, understanding their challenges, and co-creating solutions that help them thrive in today's market.</p>

      <p>By blending design insights with artisanal expertise, we help craftspeople adapt to global trends while staying true to their traditions. It's a two-way relationship where creativity flows both ways, resulting in collections that delight customers while honoring heritage.</p>

      <h2>Looking Ahead: Preserving Traditions in a Modern World</h2>

      <p>As we expand our collections, our vision is clear: to preserve artisanal traditions while making them relevant to modern living. With increasing interest in sustainable and authentic living, we believe handcrafted décor will only grow in importance.</p>

      <p>Through initiatives like Mi Casa Su Casa, we aim to not only offer beautiful creations but also create a platform that uplifts artisans, ensuring that their crafts continue to inspire future generations.</p>

      <h2>Conclusion</h2>

      <p>Behind every lamp, sculpture, and table lies the heartbeat of an artisan. Their craftsmanship transforms raw materials into soulful masterpieces, offering homes more than just beauty—offering comfort, heritage, and meaning.</p>

      <p>At Mi Casa Su Casa, we invite you to experience this craft of comfort firsthand. From art and craft pieces to sculptures and decorative items, our collections bring heritage into modern homes, proving that artistry and comfort are inseparable.</p>

      <p><strong>Because when you choose handcrafted décor, you're not just lighting your home—you're lighting the way for traditions, communities, and timeless artistry to thrive.</strong></p>
    `
  },
  "layered-living": {
    title: "Layered Living: Styling Your Space with Furniture, Lighting & Textiles",
    category: "Styling Guide",
    date: "May 1, 2023",
    image: "/Blogs_4.png",
    metaTitle: "Layered Living: Style Your Home with Furniture, Lighting & Textiles | Mi Casa Su Casa",
    metaDescription: "Discover the art of layered living. Style furniture, lighting & textiles for a harmonious, magazine-worthy home with Mi Casa Su Casa's curated collection.",
    content: `
      <p>When you walk into a beautifully styled room, you can instantly sense the harmony—it feels warm, inviting, and effortlessly chic. The secret behind that polished, magazine-worthy look isn't just about buying the most expensive pieces or following a single design trend. Instead, it's about layering. Much like fashion, where pairing the right jacket with a scarf and accessories creates a complete outfit, interiors thrive on layers of furniture, lighting, and textiles that come together to form a cohesive story.</p>

      <p>At Mi Casa Su Casa, we understand this delicate balance. From statement chairs and artisanal rugs to elegant tables paired with dramatic lamps, our curated collection offers everything you need to create a layered, harmonious living space.</p>

      <div class="my-8">
        <img src="/blog14img.jpg" alt="Layered living room design" class="w-full h-auto object-contain rounded-lg" />
      </div>

      <h2>The Art of Layering in Interior Design</h2>

      <p>Layering in interiors is about creating depth, texture, and balance. It means thoughtfully combining different elements—furniture, textiles, lighting, and accessories—so the space feels lived in, functional, and full of personality. Rather than treating each piece as a standalone, layering makes sure everything works together like chapters in a book.</p>

      <p>Think of it as composing a song: furniture forms the rhythm, textiles add melody, and lighting provides the dramatic notes that pull everything together. Without one, the harmony feels incomplete.</p>

      <h2>Furniture: The Foundation of Style</h2>

      <p>Furniture is where it all begins. Your sofas, chairs, tables, and storage units are the foundation upon which all other elements are layered.</p>

      <h3>Choosing Statement Pieces</h3>
      <p>When styling with furniture, begin with one or two statement items. It could be a bold armchair, a sculptural coffee table, or even a sleek dining table. Statement pieces ground the design and become conversation starters.</p>

      <h3>Balance in Scale and Proportion</h3>
      <p>Layering isn't about cramming multiple bold items into a single room—it's about finding balance. Pair a low-profile sofa with a taller, eye-catching floor lamp. Offset a heavy wooden dining table with light, upholstered chairs.</p>

      <div class="styling-tip">
        <p><strong>MI CASA SU CASA TIP:</strong> At Mi Casa Su Casa, we offer furniture pieces that marry heritage craftsmanship with modern silhouettes. From chairs that combine comfort with character to tables that serve as timeless anchors, our collection ensures your foundation is strong yet stylish.</p>
      </div>

      <h2>Lighting: Setting the Mood</h2>

      <p>Lighting is often called the jewelry of a room, and for good reason. It adds sparkle, drama, and functionality. Without the right lighting, even the most beautiful furniture can look flat and uninspired.</p>

      <h3>The Three Layers of Lighting</h3>
      <ul>
        <li><strong>Ambient Lighting</strong> – the main source of light in the room, such as overhead fixtures or chandeliers.</li>
        <li><strong>Task Lighting</strong> – designed for specific purposes like reading or working (table lamps, desk lamps, under-cabinet lighting).</li>
        <li><strong>Accent Lighting</strong> – decorative or dramatic elements, like floor lamps or wall sconces, that highlight artwork or corners.</li>
      </ul>

      <h3>Pairing Lamps with Furniture</h3>
      <p>A sofa looks instantly more inviting when flanked by matching table lamps. A sideboard feels complete with a dramatic pendant above it. Lighting, when layered properly, creates zones, adds warmth, and makes your furniture pieces shine.</p>

      <div class="styling-tip">
        <p><strong>MI CASA SU CASA TIP:</strong> Whether it's contemporary floor lamps, artisanal chandeliers, or minimalist pendant lights, Mi Casa Su Casa offers lighting solutions that complement your furniture choices. Imagine a handwoven rug under a sleek table, illuminated by a warm pendant—that's layered living at its best.</p>
      </div>

      <h2>Textiles: The Soul of the Space</h2>

      <p>If furniture is the structure and lighting is the sparkle, textiles are the soul. Rugs, throws, cushions, and curtains inject personality, warmth, and coziness.</p>

      <h3>Rugs as Grounding Elements</h3>
      <p>A rug is often the anchor that ties furniture together. It defines a space, especially in open-plan living. A rug beneath a coffee table and chairs can visually unite the seating area.</p>

      <h3>Cushions and Throws</h3>
      <p>Mixing cushions of varying sizes, patterns, and textures instantly elevates a sofa or armchair. Throws draped casually across furniture not only add warmth but also soften hard lines.</p>

      <h3>Curtains and Upholstery</h3>
      <p>Textiles extend to curtains, blinds, and upholstered pieces. Choosing natural fabrics like linen or cotton adds breathability, while velvets and silks bring in luxury.</p>

      <div class="styling-tip">
        <p><strong>MI CASA SU CASA TIP:</strong> Our collection at Mi Casa Su Casa includes handwoven rugs, artisanal textiles, and decorative accents that let you play with texture and pattern. Pair a neutral-toned sofa with a bold patterned rug and colorful cushions for a layered look that feels both curated and cozy.</p>
      </div>

      <h2>How to Harmonize Furniture, Lighting & Textiles</h2>

      <p>The real challenge lies in pulling these layers together seamlessly. Here's how:</p>

      <ul>
        <li><strong>Start with a Color Palette:</strong> Choose a base of 2-3 core colors and layer in accents. Neutral foundations like beige, grey, or white give you freedom to play with vibrant textiles and metallic lighting fixtures.</li>
        <li><strong>Mix Textures:</strong> A leather armchair next to a wool rug under a glass-top table adds depth. Pair rough with smooth, matte with glossy, hard with soft.</li>
        <li><strong>Play with Heights:</strong> Varying the heights of furniture and lighting creates visual interest. A low sofa paired with a tall floor lamp and mid-height side table avoids monotony.</li>
        <li><strong>Create Zones:</strong> Use rugs and lighting to define functional areas—like a reading nook with a cozy chair, side table, and lamp, or a dining zone highlighted by a pendant.</li>
        <li><strong>Don't Overcrowd:</strong> Layering is not clutter. Leave breathing room so each piece can shine. Negative space is as important as filled space.</li>
      </ul>

      <h2>The Magazine-Worthy Look</h2>

      <p>What makes some homes feel like they belong in glossy magazines? It's the attention to detail. Beyond furniture, lighting, and textiles, consider:</p>

      <ul>
        <li><strong>Art & Accessories:</strong> Sculptures, vases, and wall art add personality.</li>
        <li><strong>Greenery:</strong> Plants breathe life into interiors.</li>
        <li><strong>Personal Touches:</strong> Family heirlooms or souvenirs layered in thoughtfully tell your story.</li>
      </ul>

      <p>With these elements, the space feels authentic—stylish yet personal.</p>

      <h2>Mi Casa Su Casa: Bringing It All Together</h2>

      <p>At Mi Casa Su Casa, we specialize in helping you layer your living spaces with thoughtfully curated pieces. Whether it's furniture that grounds the room, chairs paired with artisanal rugs, or tables made complete with elegant lamps, our collection offers the tools to create spaces that are both stylish and soulful.</p>

      <p>We believe in blending craftsmanship, design, and personality so your home isn't just a place to live—it's a layered story waiting to be told.</p>

      <h2>Conclusion</h2>

      <p>Layered living is more than a design trend—it's an art form. By thoughtfully combining furniture, lighting, and textiles, you create a space that is functional, beautiful, and deeply personal. It's about creating depth, warmth, and harmony—making your home look polished while still reflecting your individuality.</p>

      <p>So, whether you're redecorating a single room or styling your entire home, remember: start with strong furniture, let lighting set the mood, and allow textiles to bring in the soul. Layer each element thoughtfully, and soon, your space will not only feel magazine-worthy but truly like home.</p>

      <p><strong>At Mi Casa Su Casa, we're here to light the way, one layered space at a time.</strong></p>
    `
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogContent[slug];

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Blog Post Not Found</h1>
          <Link to="/blog" className="text-amber-700 hover:text-amber-800">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <article>
        {/* Hero Section */}
        <section className="relative h-96 md:h-[500px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.h1
              className="text-center font-serif text-3xl md:text-5xl font-light text-white px-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{ fontFamily: 'serif' }}
            >
              {post.title}
            </motion.h1>
          </div>
        </section>

        {/* Back to Home Button */}
        <section className="bg-stone-50 py-6">
          <div className="container mx-auto px-4">
            <Link 
              to="/home"
              className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
          </div>
        </section>

        {/* Article Content */}
        <section className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              {/* Article Meta */}
              <motion.div
                className="mb-8 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <span className="text-sm font-medium uppercase tracking-wider text-amber-700">{post.category}</span>
                <p className="mt-2 text-sm text-gray-500">Published on {post.date}</p>
              </motion.div>

              {/* Article Body */}
              <motion.div
                className="blog-content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                dangerouslySetInnerHTML={{ __html: post.content }}
                style={{
                  fontFamily: 'serif',
                  fontSize: '18px',
                  lineHeight: '1.8',
                  color: '#374151'
                }}
              />

              {/* Call to Action */}
              <div className="mt-12 text-center">
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                >
                  <button 
                    className="inline-flex items-center justify-center px-8 py-3 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ 
                      backgroundColor: '#f7e8d5', 
                      color: '#292524',
                      border: 'none',
                      borderRadius: '4px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0dbc4'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f7e8d5'}
                  >
                    Explore Our Tables & Chairs Collection
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </article>

      <style jsx>{`
        .blog-content h2 {
          font-family: serif;
          font-size: 28px;
          font-weight: 500;
          color: #111827;
          margin-top: 32px;
          margin-bottom: 16px;
        }

        .blog-content ul {
          list-style-type: disc;
          list-style-position: inside;
          margin-left: 16px;
          margin-bottom: 16px;
        }

        .blog-content ul li {
          margin-bottom: 8px;
        }

        .blog-content p {
          margin-bottom: 24px;
        }

        .blog-content .styling-tip {
          background-color: #fafaf9;
          padding: 24px;
          border-radius: 8px;
          margin: 24px 0;
          border-left: 4px solid #d97706;
        }

        .blog-content .styling-tip p {
          margin-bottom: 0;
          font-style: italic;
        }

        .blog-content .my-8 {
          margin: 32px 0;
        }

        .blog-content img {
          width: 100%;
          height: auto;
          object-fit: contain;
          border-radius: 8px;
          max-width: 100%;
        }
      `}</style>
    </div>
  );
}